import { useRef, useState, useEffect, useContext } from "react";
import { Checkbox, Panel, DefaultButton, TextField, SpinButton, Dropdown, IDropdownOption, DropdownMenuItemType } from "@fluentui/react";
import { SparkleFilled } from "@fluentui/react-icons";
import logo_chat from "../../assets/logo_chat.png";
import { useParams, useNavigate } from "react-router-dom";
import ReactDOMServer from "react-dom/server";
import { format } from "date-fns/format";
import uuid from "react-uuid";
import Swal from "sweetalert2";
import Encuesta from "../../components/Encuesta/Encuesta";
import styles from "./Chat.module.css";

import {
    AskResponse,
    ChatRequest,
    NewChatRequest,
    ChatTurn,
    Feedback,
    createChat,
    chatApiV2,
    uploadImage,
    createFeedback // FIX: importar createFeedback desde ../../api
} from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { ClearChatButton } from "../../components/ClearChatButton";

import ModalLike from "../../components/Answer/Modal";
import { ModalDisLike } from "../../components/Answer/Modal";
import { AppStateContext } from "../../components/state/AppProvider";

import Layout from "../layout/Layout";
import { set } from "date-fns";

import { useQuery, useQueryClient } from "react-query";

const getMessages = async (chatId: string | undefined) => {
    if (!chatId) {
        return [];
    }

    try {
        const response = await fetch(`/get_messages_by_chat/${chatId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            }
        });

        if (!response.ok) {
            const error = await response.json();

            throw new Error(error.message || "Error fetching usage data");
        }

        return await response.json();
    } catch (e) {
        console.log(`Error: ${e}`);
        return [];
    }
};

const getChat = async (chatId: string | undefined) => {
    if (!chatId) {
        return null;
    }

    try {
        const response = await fetch(`/get_chat/${chatId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            }
        });

        if (!response.ok) {
            const error = await response.json();

            throw new Error(error.message || "Error fetching usage data");
        }

        return await response.json();
    } catch (e) {
        console.log(`Error: ${e}`);
        return [];
    }
};

const Chat = () => {
    const { chatId } = useParams<{ chatId: string }>();

    const appStateContext = useContext(AppStateContext);
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [retrieveCount, setRetrieveCount] = useState<number>(10);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(false);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [modelSelection, setModel] = useState<string>("gpt-4");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(false);
    const abortFuncs = useRef([] as AbortController[]);
    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [loadingfirstmessage, setloadingfirstmessage] = useState<boolean>(false);
    const [disablewriting, setdisablewriting] = useState<boolean>(false);

    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);
    const [activeAnalysisPanelTabFirstCitation, setactiveAnalysisPanelTabFirstCitation] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    type UserMessage = {
        message: string;
        image?: string | null;
    };
    const [answers, setAnswers] = useState<[user: UserMessage, response: AskResponse][]>([]);

    const [showLikeModal, setshowLikeModal] = useState(false);
    const [showDislikeModal, setshowDislikeModal] = useState(false);

    const userDataString = sessionStorage.getItem("userData");
    const userData = JSON.parse(userDataString ?? "{}");

    const [encuestaHabilitada, setEncuestaHabilitada] = useState(true);

    const [documentos, setdocumentos] = useState<string>();
    const { data: chatData, isLoading: isChatLoading, error: chatError } = useQuery(["chat", chatId], () => getChat(chatId));
    const { data: messagesData, isLoading: isMessagesLoading, error: messagesError } = useQuery(["messages", chatId], () => getMessages(chatId));
    const queryClient = useQueryClient();
    const [selectedMessageFeedback, setSelectedMessageFeedback] = useState<number>(0);

    // Resolver de forma robusta el message_id que corresponde a la respuesta mostrada
    const resolveMessageIdForAnswer = (index: number): string | null => {
        try {
            const ansText = (answers[index]?.[1]?.answer || "").trim();
            const list = (messagesData as any[]) || [];
            if (!list.length) return null;

            // 1) Intento por coincidencia exacta de contenido
            for (let i = list.length - 1; i >= 0; i--) {
                const m = list[i];
                if ((m?.type === "SYSTEM" || m?.type === "BOT" || !m?.type) && typeof m?.content === "string") {
                    if ((m.content || "").trim() === ansText) {
                        return m.id;
                    }
                }
            }

            // 2) Fallback posicional: [SYSTEM bienvenida] + (USER, SYSTEM)*N
            const fallbackIdx = (index + 1) * 2;
            if (list[fallbackIdx]?.id) {
                return list[fallbackIdx].id;
            }

            // 3) Último SYSTEM reciente
            for (let i = list.length - 1; i >= 0; i--) {
                const m = list[i];
                if (m?.type === "SYSTEM" && m?.id) return m.id;
            }
        } catch (e) {
            console.warn("No se pudo resolver message_id para el feedback:", e);
        }
        return null;
    };

    const handleSendMessage = async (question: string) => {
        lastQuestionRef.current = question;
        if (error) setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        if (!chatId) {
            Swal.fire({ title: "Error", text: "No se pudo obtener el ID del chat.", icon: "error", confirmButtonText: "Aceptar" });
            setIsLoading(false);
            return;
        }

        // Verifica si la imagen es la misma que la enviada anteriormente
        if (selectedImage) {
            const imageId = selectedImage.name + selectedImage.size + selectedImage.lastModified;
            if (imageId === lastImageSent) {
                Swal.fire({
                    icon: "warning",
                    title: "Imagen ya enviada",
                    text: "No puedes enviar la misma imagen dos veces seguidas."
                });
                setIsLoading(false);
                return;
            }
            setLastImageSent(imageId);
        }

        // Solo agrega el mensaje del usuario una vez
        setAnswers(prev => [
            ...prev,
            [
                { message: question, image: imagePreviewUrl },
                { answer: "", thoughts: "", data_points: [], id: uuid() }
            ]
        ]);
        setSelectedImage(null);
        setImagePreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = "";

        try {
            let result: AskResponse;
            if (selectedImage) {
                result = await uploadImage(chatId, selectedImage, question);
            } else {
                const request: NewChatRequest = { chat_id: chatId, message: question };
                result = await chatApiV2(request);
            }
            // Actualiza solo la respuesta del último mensaje
            setAnswers(prev => {
                const updated = [...prev];
                updated[updated.length - 1][1] = result;
                return updated;
            });
            // Refresca mensajes para que el feedback pueda localizar el message_id
            queryClient.invalidateQueries(["messages"]);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [lastImageSent, setLastImageSent] = useState<string | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        if (file) {
            const fileId = file.name + file.size + file.lastModified;
            if (fileId === lastImageSent) {
                Swal.fire({
                    icon: "warning",
                    title: "Imagen ya enviada",
                    text: "No puedes enviar la misma imagen dos veces seguidas."
                });
                // opcional: limpiamos el input
                e.target.value = "";
                return;
            }
            setSelectedImage(file);
            setImagePreviewUrl(URL.createObjectURL(file));
        }
    };
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    useEffect(() => {
        const obtenerdocumentos = async () => {
            const userProduct = sessionStorage.getItem("CodigoProducto") ?? "";
            const userId = sessionStorage.getItem("userEmail") || "";
            const userName = sessionStorage.getItem("userName") || "";

            const result = await createChat(userProduct, userId, userName);
            if (result) {
                setdisablewriting(false);
            }
            setloadingfirstmessage(false);
            setdocumentos(result.initial_message.content);
            queryClient.invalidateQueries(["messages"]);
        };

        setloadingfirstmessage(true);
        setdisablewriting(true);

        if (!chatError && !isChatLoading && chatData) {
            if (chatData.itemId) {
                sessionStorage.setItem("CodigoProducto", chatData.itemId);
            }
        }
        if (!messagesError && !isMessagesLoading && messagesData) {
            setdisablewriting(false);
            setloadingfirstmessage(false);
            setdocumentos(messagesData[0]?.content || "");
        } else {
            console.error("Error fetching messages:", messagesError);
        }
        //obtenerdocumentos();
    }, [chatData, messagesData]);

    useEffect(() => {
        setshowLikeModal(appStateContext?.state.OpenLikeModal!);
        if (!appStateContext?.state.OpenLikeModal! && appStateContext?.state.MessageSelected != -1) {
            const idMensajeBuscado = appStateContext?.state.MessageSelected ?? 0;

            // Resolver de forma robusta el ID del mensaje de la respuesta
            const messageId = resolveMessageIdForAnswer(idMensajeBuscado);
            if (!messageId) {
                console.warn("No se resolvió message_id para feedback LIKE");
                return;
            }

            const ratings: any = {
                user_id: userData?.Id ?? "000000",
                type: "like",
                comment: appStateContext?.state.Comment ?? "",
                message_id: messageId,
                chat_id: chatId // asociar feedback al chat_id
            };

            appStateContext?.dispatch({ type: "SET_Rating_MODEL", payload: ratings });
            makeRatingRequest(ratings);
        }
    }, [appStateContext?.state.OpenLikeModal]);

    useEffect(() => {
        setshowDislikeModal(appStateContext?.state.OpenDislikeModal!);
        if (!appStateContext?.state.OpenDislikeModal! && appStateContext?.state.MessageSelected != -1) {
            const idMensajeBuscado = appStateContext?.state.MessageSelected ?? 0;

            // Resolver de forma robusta el ID del mensaje de la respuesta
            const messageId = resolveMessageIdForAnswer(idMensajeBuscado);
            if (!messageId) {
                console.warn("No se resolvió message_id para feedback DISLIKE");
                return;
            }

            const ratings: any = {
                user_id: userData?.Id ?? "000000",
                type: "dislike",
                comment: appStateContext?.state.Comment ?? "",
                message_id: messageId,
                chat_id: chatId // asociar feedback al chat_id
            };

            appStateContext?.dispatch({ type: "SET_Rating_MODEL", payload: ratings });
            makeRatingRequest(ratings);
        }
    }, [appStateContext?.state.OpenDislikeModal]);

    const makeRatingRequest = async (rating: Feedback | any) => {
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);

        try {
            const response = await createFeedback(rating); // FIX: ahora llama al API correcto
            console.log("Feedback response:", response);
        } catch (e) {
            if (!abortController.signal.aborted) {
                let errorMessage = "Ocurrió un error. Inténtalo de nuevo. Si el problema persiste, comuníquese con el administrador del sitio.";
                console.log(errorMessage, e);
            }
        } finally {
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
        }

        return abortController.abort();
    };

    const makeApiRequest = async (question: string) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        try {
            if (!chatId) {
                Swal.fire({
                    title: "Error",
                    text: "No se pudo obtener el ID del chat.",
                    icon: "error",
                    confirmButtonText: "Aceptar"
                });
                return;
            }

            const request: NewChatRequest = {
                chat_id: chatId,
                message: question
            };

            const result = await chatApiV2(request);
            setAnswers([...answers, [{ message: question }, result]]);

            queryClient.invalidateQueries(["messages"]);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = () => {
        lastQuestionRef.current = "Hola";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
    };

    // Las llaves son necesarias. Sin ellas el efecto retorna el valor de scrollIntoView, y
    // desde Chromium 150 esa API devuelve una Promise cuando behavior es "smooth". React
    // tomaba esa Promise como funcion de limpieza e intentaba invocarla al cambiar isLoading,
    // lanzando "destroy is not a function" y desmontando la aplicacion completa.
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "3"));
    };

    const onUseSemanticRankerChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticRanker(!!checked);
    };

    const onUseSemanticCaptionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticCaptions(!!checked);
    };

    const onExcludeCategoryChanged = (_ev?: React.FormEvent, newValue?: string) => {
        setExcludeCategory(newValue || "");
    };

    const onModelSelection = (_ev?: React.FormEvent, selection?: IDropdownOption) => {
        if (selection) {
            setModel(selection.key.toString() || "gpt-4");
        } else {
            const defaultValue: string = "gpt-4";
            setModel(defaultValue || "gpt-4");
        }
    };

    const onUseSuggestFollowupQuestionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSuggestFollowupQuestions(!!checked);
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onShowFIRSTCitation = (citation: string, index: number, doctype?: string) => {
        // Extract filename from citation
        const filename = citation.replace("/content2/", "");
        // Build new citation URL with doctype
        const citationWithType = doctype
            ? `/content2/${doctype}/${filename}`
            : citation;

        if (
            activeCitation === citationWithType &&
            activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab &&
            selectedAnswer === index
        ) {
            setactiveAnalysisPanelTabFirstCitation(undefined);
        } else {
            setActiveCitation(citationWithType);
            setactiveAnalysisPanelTabFirstCitation(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab2 = (tab: AnalysisPanelTabs, index: number) => {
        console.log("SELECTED");
        setactiveAnalysisPanelTabFirstCitation(undefined);
    };
    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        console.log("LOG");
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const options: IDropdownOption[] = [
        { key: "GPT 3", text: "GPT 3", itemType: DropdownMenuItemType.Header },
        { key: "gpt35turbo", text: "GPT3.5 TURBO" },
        { key: "divider_1", text: "-", itemType: DropdownMenuItemType.Divider },
        { key: "GPT 4", text: "GPT 4", itemType: DropdownMenuItemType.Header },
        { key: "gpt-4", text: "GPT 4 - 8000 Tokens" },
        { key: "gpt4-32k", text: "GPT 4 - 32000 Tokens" }
    ];

    const navigate = useNavigate();

    const logOut = () => {
        sessionStorage.removeItem("authToken");
        navigate("/auth");
    };

    const [restartConversation, setRestartConversation] = useState(false);

    const handleRestartConversation = () => {
        clearChat();
        setRestartConversation(true);
    };

    useEffect(() => {
        if (restartConversation) {
            setRestartConversation(false);
        }
    }, [restartConversation]);

    return (
        <>
            <div className={styles.container}>
                {<style>{`.${styles.container} { background-image: none; }`}</style>}

                <ClearChatButton className={styles.commandButton} onClick={clearChat} disabled={!lastQuestionRef.current || isLoading} />

                <Layout backgroundImageChanged={true} onClick={() => {}} onRestartConversation={handleRestartConversation} className={styles.commandButton2} />

                <div className={styles.chatRoot}>
                    <div className={styles.chatContainer}>
                        <div className={styles.chatMessageStream}>
                            {documentos && (
                                <div key={"0"}>
                                    <div className={styles.chatMessageGpt}>
                                        <Answer
                                            id={0}
                                            key={"0"}
                                            answer={{ answer: documentos ?? "", data_points: [], thoughts: "", id: "1" }}
                                            isSelected={selectedAnswer === 0 && activeAnalysisPanelTab !== undefined}
                                            onCitationClicked={c => onShowFIRSTCitation(c, 0)}
                                            onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, 0)}
                                            onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, 0)}
                                            onFollowupQuestionClicked={q => makeApiRequest(q)}
                                            showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === 0}
                                        />
                                    </div>
                                </div>
                            )}

                            {answers.map(
                                (answer, index) =>
                                    answer[0].message !== documentos && (
                                        <div key={index} onClick={() => setSelectedMessageFeedback(index)}>
                                            <UserChatMessage message={answer[0].message} image={answer[0].image} />

                                            {answer[1].answer && (
                                                <div className={styles.chatMessageGpt}>
                                                    <Answer
                                                        id={index}
                                                        key={index}
                                                        answer={answer[1]}
                                                        isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                        onCitationClicked={c => onShowFIRSTCitation(c, index)}
                                                        onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                        onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                        onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                        showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )
                            )}
                            {loadingfirstmessage && (
                                <>
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading />
                                    </div>
                                </>
                            )}
                            {isLoading && (
                                <>
                                    {!answers.some(a => a[0].message === lastQuestionRef.current) && <UserChatMessage message={lastQuestionRef.current} />}
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading />
                                    </div>
                                </>
                            )}
                            {error ? (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>

                        <div className={styles.chatInput}>
                            {imagePreviewUrl && (
                                <div className={styles.imagePreviewContainer}>
                                    <img src={imagePreviewUrl} alt="preview" className={styles.imagePreview} />
                                    <button
                                        type="button"
                                        className={styles.removeImageButton}
                                        onClick={() => {
                                            setSelectedImage(null);
                                            setImagePreviewUrl(null);
                                        }}
                                    >
                                        ✖
                                    </button>
                                </div>
                            )}

                            <div className={styles.inputBar}>
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                    className={styles.clipButton}
                                    title="Adjuntar imagen"
                                >
                                    📎
                                </button>
                                <input type="file" accept="image/*" style={{ display: "none" }} ref={fileInputRef} onChange={handleFileChange} />
                                <QuestionInput
                                    clearOnSend
                                    placeholder="¿Cómo puedo ayudarte?"
                                    disabled={isLoading}
                                    onSend={question => handleSendMessage(question)}
                                    loadingfirstmessage={disablewriting}
                                />
                            </div>
                        </div>
                    </div>

                    <Panel
                        headerText="Configure answer generation"
                        isOpen={isConfigPanelOpen}
                        isBlocking={false}
                        onDismiss={() => setIsConfigPanelOpen(false)}
                        closeButtonAriaLabel="Close"
                        onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>Close</DefaultButton>}
                        isFooterAtBottom={true}
                    >
                        <TextField
                            className={styles.chatSettingsSeparator}
                            defaultValue={promptTemplate}
                            label="Override prompt template"
                            multiline
                            autoAdjustHeight
                            onChange={onPromptTemplateChange}
                        />

                        <SpinButton
                            className={styles.chatSettingsSeparator}
                            label="Retrieve this many documents from search:"
                            min={1}
                            max={50}
                            defaultValue={retrieveCount.toString()}
                            onChange={onRetrieveCountChange}
                        />
                        <TextField className={styles.chatSettingsSeparator} label="Exclude category" onChange={onExcludeCategoryChanged} />
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useSemanticRanker}
                            label="Use semantic ranker for retrieval"
                            onChange={onUseSemanticRankerChange}
                        />
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useSemanticCaptions}
                            label="Use query-contextual summaries instead of whole documents"
                            onChange={onUseSemanticCaptionsChange}
                            disabled={!useSemanticRanker}
                        />
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useSuggestFollowupQuestions}
                            label="Suggest follow-up questions"
                            onChange={onUseSuggestFollowupQuestionsChange}
                        />
                        <Dropdown
                            className={styles.chatSettingsSeparator}
                            label="Model"
                            options={options}
                            defaultSelectedKey={modelSelection}
                            onChange={onModelSelection}
                        />
                    </Panel>
                </div>
                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={activeCitation}
                        onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                        citationHeight="810px"
                        answer={answers[selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                    />
                )}

                {activeAnalysisPanelTabFirstCitation && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={activeCitation}
                        onActiveTabChanged={x => onToggleTab2(x, selectedAnswer)}
                        citationHeight="810px"
                        answer={{ answer: documentos ?? "", data_points: [], thoughts: "", id: "1" }}
                        activeTab={activeAnalysisPanelTabFirstCitation}
                    />
                )}
                {encuestaHabilitada ? <Encuesta /> : null}
                {showLikeModal ? <ModalLike></ModalLike> : <></>}
                {showDislikeModal ? <ModalDisLike></ModalDisLike> : <></>}
            </div>
        </>
    );
};

export default Chat;
